import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Index from "./pages/Index.tsx";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { I18nProvider } from "@/contexts/I18nContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import CheckoutResumer from "@/components/CheckoutResumer";
import { isAutomationMode } from "@/lib/automationMode";

// Lazy-loaded pages
const TestPage = lazy(() => import("./pages/TestPage.tsx"));
const ResultsPage = lazy(() => import("./pages/ResultsPage.tsx"));
const WrongAnswersPage = lazy(() => import("./pages/WrongAnswersPage.tsx"));
const PracticePage = lazy(() => import("./pages/PracticePage.tsx"));
const ScoreChart = lazy(() => import("./pages/ScoreChart.tsx"));
const ScoreConverterPage = lazy(() => import("./pages/ScoreConverterPage.tsx"));
const Vocabulary = lazy(() => import("./pages/Vocabulary.tsx"));
const FAQ = lazy(() => import("./pages/FAQ.tsx"));
const TipsListening = lazy(() => import("./pages/TipsListening.tsx"));
const TipsReading = lazy(() => import("./pages/TipsReading.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const AIPracticePage = lazy(() => import("./pages/AIPracticePage.tsx"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.tsx"));
const TermsOfService = lazy(() => import("./pages/TermsOfService.tsx"));
const About = lazy(() => import("./pages/About.tsx"));
const GrammarPage = lazy(() => import("./pages/GrammarPage.tsx"));
const GrammarDetailPage = lazy(() => import("./pages/GrammarDetailPage.tsx"));
const StatsPage = lazy(() => import("./pages/StatsPage.tsx"));
const RecommendedBooks = lazy(() => import("./pages/RecommendedBooks.tsx"));
const PricingPage = lazy(() => import("./pages/PricingPage.tsx"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy.tsx"));
const MySubscriptionPage = lazy(() => import("./pages/MySubscriptionPage.tsx"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage.tsx"));
const ReviewPage = lazy(() => import("./pages/ReviewPage.tsx"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage.tsx"));
const PaymentCancelPage = lazy(() => import("./pages/PaymentCancelPage.tsx"));
const RoundDetailPage = lazy(() => import("./pages/RoundDetailPage.tsx"));
const TipsPartPage = lazy(() => import("./pages/TipsPartPage.tsx"));
const StudyPlan7Days = lazy(() => import("./pages/StudyPlan7Days.tsx"));
const TrapPatterns = lazy(() => import("./pages/TrapPatterns.tsx"));
const ScoreGoalGuidePage = lazy(() => import("./pages/ScoreGoalGuidePage.tsx"));
const BeginnerGuidePage = lazy(() => import("./pages/BeginnerGuidePage.tsx"));
const QuestionPoolPage = lazy(() => import("./pages/QuestionPoolPage.tsx"));
const PassageDetailPage = lazy(() => import("./pages/PassageDetailPage.tsx"));
const QuestionDetailPage = lazy(() => import("./pages/QuestionDetailPage.tsx"));
const SpeakingPracticePage = lazy(() => import("./pages/SpeakingPracticePage.tsx"));
const SpeakingDetailPage = lazy(() => import("./pages/SpeakingDetailPage.tsx"));
const WritingPracticePage = lazy(() => import("./pages/WritingPracticePage.tsx"));
const WritingDetailPage = lazy(() => import("./pages/WritingDetailPage.tsx"));
const CATTestPage = lazy(() => import("./pages/CATTestPage.tsx"));
const StandaloneQuestionPage = lazy(() => import("./pages/StandaloneQuestionPage.tsx"));
const GuideIndexPage = lazy(() => import("./pages/GuideIndexPage.tsx"));
const LongTailPage = lazy(() => import("./pages/LongTailPage.tsx"));
const ToeflHubPage = lazy(() => import("./pages/ToeflHubPage.tsx"));
const HubPage = lazy(() => import("./pages/HubPage.tsx"));
const SelfStudyRoadmapPage = lazy(() => import("./pages/SelfStudyRoadmapPage.tsx"));
const FreeMockTestPage = lazy(() => import("./pages/FreeMockTestPage.tsx"));
const MockTestPage = lazy(() => import("./pages/MockTestPage.tsx"));
const MockTestSectionRunnerPage = lazy(() => import("./pages/MockTestSectionRunnerPage.tsx"));
const HtmlSitemapPage = lazy(() => import("./pages/HtmlSitemapPage.tsx"));
const AdminAlertsPage = lazy(() => import("./pages/AdminAlertsPage.tsx"));
const AdminDashboardPage = lazy(() => import("./pages/AdminDashboardPage.tsx"));
const AIFeedbackHistoryPage = lazy(() => import("./pages/AIFeedbackHistoryPage.tsx"));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage.tsx"));
const BookmarksPage = lazy(() => import("./pages/BookmarksPage.tsx"));
const TopicsPage = lazy(() => import("./pages/TopicsPage.tsx"));
const QuestionTypePage = lazy(() => import("./pages/QuestionTypePage.tsx"));
const QualityStandardsPage = lazy(() => import("./pages/QualityStandardsPage.tsx"));
const DiagnosePage = lazy(() => import("./pages/DiagnosePage.tsx"));
const WritingScoreComparisonPage = lazy(() => import("./pages/WritingScoreComparisonPage.tsx"));
const MockTestComparisonPage = lazy(() => import("./pages/MockTestComparisonPage.tsx"));
const NewFormat2026Page = lazy(() => import("./pages/NewFormat2026Page.tsx"));
const MockTestRoundsHubPage = lazy(() => import("./pages/MockTestRoundsHubPage.tsx"));
const MockTestSetsPage = lazy(() => import("./pages/MockTestSetsPage.tsx"));
const AIScoringReliabilityPage = lazy(() => import("./pages/AIScoringReliabilityPage.tsx"));
const VideoLessonsPage = lazy(() => import("./pages/VideoLessonsPage.tsx"));
const Contact = lazy(() => import("./pages/Contact.tsx"));
const MobileBottomNav = lazy(() => import("@/components/MobileBottomNav"));
const OnboardingRedirect = lazy(() => import("@/components/OnboardingRedirect"));
const PushPermissionBanner = lazy(() => import("@/components/PushPermissionBanner"));
const PWAInstallBanner = lazy(() => import("@/components/PWAInstallBanner"));
const FeedbackWidget = lazy(() => import("@/components/FeedbackWidget"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 60 * 1000, // 30 minutes — most content is static/prerendered
      gcTime: 60 * 60 * 1000, // 1 hour
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  },
});

const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center px-4">
    <div className="w-full max-w-3xl rounded-2xl border border-border bg-card/80 p-6 md:p-8">
      <h1 className="sr-only">Power TOEFL</h1>
      <div className="h-8 w-40 rounded-md bg-muted animate-pulse mb-6" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="h-24 rounded-xl bg-muted animate-pulse" />
        <div className="h-24 rounded-xl bg-muted animate-pulse" />
      </div>
      <div className="space-y-3">
        <div className="h-4 w-full rounded bg-muted animate-pulse" />
        <div className="h-4 w-5/6 rounded bg-muted animate-pulse" />
        <div className="h-4 w-2/3 rounded bg-muted animate-pulse" />
      </div>
      <p className="text-xs text-muted-foreground mt-5">Preparing your TOEFL workspace...</p>
    </div>
  </div>
);

/** All app routes — rendered once for default lang (ko) and once per non-default lang prefix */
const AppRoutes = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/" element={<Index />} />
      <Route path="/test/:section/:questionNum" element={<TestPage />} />
      <Route path="/test/:section/:questionNum/:roundId" element={<TestPage />} />
      <Route path="/results/:section" element={<ResultsPage />} />
      <Route path="/wrong-answers" element={<WrongAnswersPage />} />
      <Route path="/practice/rounds" element={<MockTestRoundsHubPage />} />
      <Route path="/practice/:partId" element={<PracticePage />} />
      <Route path="/ai-practice" element={<AIPracticePage />} />
      <Route path="/ai-feedback-history" element={<AIFeedbackHistoryPage />} />
      <Route path="/score-chart" element={<ScoreChart />} />
      <Route path="/score-converter" element={<ScoreConverterPage />} />
      <Route path="/vocabulary" element={<Vocabulary />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/tips/listening" element={<TipsListening />} />
      <Route path="/tips/reading" element={<TipsReading />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/grammar" element={<GrammarPage />} />
      <Route path="/grammar/:topicId" element={<GrammarDetailPage />} />
      <Route path="/stats" element={<StatsPage />} />
      <Route path="/books" element={<RecommendedBooks />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/refund" element={<RefundPolicy />} />
      <Route path="/refund-policy" element={<Navigate to="/refund" replace />} />
      <Route path="/writing" element={<WritingPracticePage />} />
      <Route path="/writing/:writingSlug" element={<WritingDetailPage />} />
      <Route path="/my-subscription" element={<MySubscriptionPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/review" element={<ReviewPage />} />
      <Route path="/payment-success" element={<PaymentSuccessPage />} />
      <Route path="/payment-cancel" element={<PaymentCancelPage />} />
      <Route path="/test/round/:roundId" element={<RoundDetailPage />} />
      <Route path="/tips/section/:sectionId" element={<TipsPartPage />} />

      <Route path="/tips/7-day-plan" element={<StudyPlan7Days />} />
      <Route path="/tips/trap-patterns" element={<TrapPatterns />} />
      <Route path="/tips/score-guide" element={<ScoreGoalGuidePage />} />
      <Route path="/tips/beginner-guide" element={<BeginnerGuidePage />} />
      <Route path="/speaking" element={<SpeakingPracticePage />} />
      <Route path="/speaking/:speakingSlug" element={<SpeakingDetailPage />} />
      <Route path="/questions" element={<QuestionPoolPage />} />
      <Route path="/questions/speaking" element={<SpeakingPracticePage />} />
      <Route path="/questions/speaking/:speakingSlug" element={<SpeakingDetailPage />} />
      <Route path="/questions/writing" element={<WritingPracticePage />} />
      <Route path="/questions/writing/:writingSlug" element={<WritingDetailPage />} />
      <Route path="/questions/cat" element={<CATTestPage />} />
      <Route path="/questions/q/:questionSlug" element={<StandaloneQuestionPage />} />
      <Route path="/questions/:slug" element={<PassageDetailPage />} />
      <Route path="/questions/:slug/:questionSlug" element={<QuestionDetailPage />} />
      <Route path="/toefl-guide" element={<ToeflHubPage />} />
      <Route path="/hub/:slug" element={<HubPage />} />
      <Route path="/guide" element={<GuideIndexPage />} />
      <Route path="/guide/writing-score-comparison" element={<WritingScoreComparisonPage />} />
      <Route path="/guide/toefl-mock-test-comparison" element={<MockTestComparisonPage />} />
      <Route path="/guide/:slug" element={<LongTailPage />} />
      <Route path="/tips/self-study-roadmap" element={<SelfStudyRoadmapPage />} />
      <Route path="/free-mock-test" element={<FreeMockTestPage />} />
      <Route path="/mock-test" element={<MockTestPage />} />
      <Route path="/mock-test/run/:section" element={<MockTestSectionRunnerPage />} />
      <Route path="/sitemap" element={<HtmlSitemapPage />} />
      <Route path="/admin/alerts" element={<AdminAlertsPage />} />
      <Route path="/admin" element={<AdminDashboardPage />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/bookmarks" element={<BookmarksPage />} />
      <Route path="/topics" element={<TopicsPage />} />
      <Route path="/topics/:topicSlug" element={<TopicsPage />} />
      <Route path="/questions/type/:typeSlug" element={<QuestionTypePage />} />
      <Route path="/quality-standards" element={<QualityStandardsPage />} />
      <Route path="/diagnose" element={<DiagnosePage />} />
      <Route path="/tips/new-format-2026" element={<NewFormat2026Page />} />
      <Route path="/mock-test/sets" element={<MockTestSetsPage />} />
      <Route path="/guide/ai-scoring-reliability" element={<AIScoringReliabilityPage />} />
      <Route path="/lessons" element={<VideoLessonsPage />} />

      {/* Localized routes: /ja/*, /th/*, /vi/* */}
      <Route path="/:lang" element={<Index />} />
      <Route path="/:lang/test/:section/:questionNum" element={<TestPage />} />
      <Route path="/:lang/test/:section/:questionNum/:roundId" element={<TestPage />} />
      <Route path="/:lang/results/:section" element={<ResultsPage />} />
      <Route path="/:lang/wrong-answers" element={<WrongAnswersPage />} />
      <Route path="/:lang/practice/rounds" element={<MockTestRoundsHubPage />} />
      <Route path="/:lang/practice/:partId" element={<PracticePage />} />
      <Route path="/:lang/ai-practice" element={<AIPracticePage />} />
      <Route path="/:lang/ai-feedback-history" element={<AIFeedbackHistoryPage />} />
      <Route path="/:lang/score-chart" element={<ScoreChart />} />
      <Route path="/:lang/score-converter" element={<ScoreConverterPage />} />
      <Route path="/:lang/vocabulary" element={<Vocabulary />} />
      <Route path="/:lang/faq" element={<FAQ />} />
      <Route path="/:lang/tips/listening" element={<TipsListening />} />
      <Route path="/:lang/tips/reading" element={<TipsReading />} />
      <Route path="/:lang/privacy" element={<PrivacyPolicy />} />
      <Route path="/:lang/terms" element={<TermsOfService />} />
      <Route path="/:lang/about" element={<About />} />
      <Route path="/:lang/contact" element={<Contact />} />
      <Route path="/:lang/grammar" element={<GrammarPage />} />
      <Route path="/:lang/grammar/:topicId" element={<GrammarDetailPage />} />
      <Route path="/:lang/stats" element={<StatsPage />} />
      <Route path="/:lang/books" element={<RecommendedBooks />} />
      <Route path="/:lang/pricing" element={<PricingPage />} />
      <Route path="/:lang/refund" element={<RefundPolicy />} />
      <Route path="/:lang/refund-policy" element={<Navigate to="../refund" replace />} />
      <Route path="/:lang/writing" element={<WritingPracticePage />} />
      <Route path="/:lang/writing/:writingSlug" element={<WritingDetailPage />} />
      <Route path="/:lang/my-subscription" element={<MySubscriptionPage />} />
      <Route path="/:lang/reset-password" element={<ResetPasswordPage />} />
      <Route path="/:lang/review" element={<ReviewPage />} />
      <Route path="/:lang/payment-success" element={<PaymentSuccessPage />} />
      <Route path="/:lang/payment-cancel" element={<PaymentCancelPage />} />
      <Route path="/:lang/test/round/:roundId" element={<RoundDetailPage />} />
      <Route path="/:lang/tips/section/:sectionId" element={<TipsPartPage />} />

      <Route path="/:lang/tips/7-day-plan" element={<StudyPlan7Days />} />
      <Route path="/:lang/tips/trap-patterns" element={<TrapPatterns />} />
      <Route path="/:lang/tips/score-guide" element={<ScoreGoalGuidePage />} />
      <Route path="/:lang/tips/beginner-guide" element={<BeginnerGuidePage />} />
      <Route path="/:lang/speaking" element={<SpeakingPracticePage />} />
      <Route path="/:lang/speaking/:speakingSlug" element={<SpeakingDetailPage />} />
      <Route path="/:lang/questions" element={<QuestionPoolPage />} />
      <Route path="/:lang/questions/speaking" element={<SpeakingPracticePage />} />
      <Route path="/:lang/questions/speaking/:speakingSlug" element={<SpeakingDetailPage />} />
      <Route path="/:lang/questions/writing" element={<WritingPracticePage />} />
      <Route path="/:lang/questions/writing/:writingSlug" element={<WritingDetailPage />} />
      <Route path="/:lang/questions/cat" element={<CATTestPage />} />
      <Route path="/:lang/questions/q/:questionSlug" element={<StandaloneQuestionPage />} />
      <Route path="/:lang/questions/:slug" element={<PassageDetailPage />} />
      <Route path="/:lang/questions/:slug/:questionSlug" element={<QuestionDetailPage />} />
      <Route path="/:lang/toefl-guide" element={<ToeflHubPage />} />
      <Route path="/:lang/hub/:slug" element={<HubPage />} />
      <Route path="/:lang/guide" element={<GuideIndexPage />} />
      <Route path="/:lang/guide/writing-score-comparison" element={<WritingScoreComparisonPage />} />
      <Route path="/:lang/guide/toefl-mock-test-comparison" element={<MockTestComparisonPage />} />
      <Route path="/:lang/guide/:slug" element={<LongTailPage />} />
      <Route path="/:lang/tips/self-study-roadmap" element={<SelfStudyRoadmapPage />} />
      <Route path="/:lang/free-mock-test" element={<FreeMockTestPage />} />
      <Route path="/:lang/mock-test" element={<MockTestPage />} />
      <Route path="/:lang/mock-test/run/:section" element={<MockTestSectionRunnerPage />} />
      <Route path="/:lang/sitemap" element={<HtmlSitemapPage />} />
      <Route path="/:lang/admin/alerts" element={<AdminAlertsPage />} />
      <Route path="/:lang/admin" element={<AdminDashboardPage />} />
      <Route path="/:lang/onboarding" element={<OnboardingPage />} />
      <Route path="/:lang/bookmarks" element={<BookmarksPage />} />
      <Route path="/:lang/topics" element={<TopicsPage />} />
      <Route path="/:lang/topics/:topicSlug" element={<TopicsPage />} />
      <Route path="/:lang/questions/type/:typeSlug" element={<QuestionTypePage />} />
      <Route path="/:lang/quality-standards" element={<QualityStandardsPage />} />
      <Route path="/:lang/diagnose" element={<DiagnosePage />} />
      <Route path="/:lang/lessons" element={<VideoLessonsPage />} />
      <Route path="/:lang/tips/new-format-2026" element={<NewFormat2026Page />} />
      <Route path="/:lang/mock-test/sets" element={<MockTestSetsPage />} />
      <Route path="/:lang/guide/ai-scoring-reliability" element={<AIScoringReliabilityPage />} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  </Suspense>
);

const App = () => {
  const [showDeferredUi, setShowDeferredUi] = useState(false);
  const suppressInteractiveUi = isAutomationMode();

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let idleId: number | undefined;

    const reveal = () => setShowDeferredUi(true);

    if ("requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(reveal, { timeout: 2000 });
    } else {
      timeoutId = setTimeout(reveal, 1200);
    }

    return () => {
      if (idleId !== undefined && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, []);

  // Global study-time tracker — accumulates active seconds in localStorage.
  useEffect(() => {
    if (suppressInteractiveUi) return;
    let stop: (() => void) | undefined;
    const t = window.setTimeout(() => {
      import("@/lib/studyTime").then(({ startStudyTracker }) => {
        stop = startStudyTracker();
      });
      import("@/lib/funnelTracker").then(({ trackFunnel }) => {
        trackFunnel("visit", { ref: document.referrer || null }, { onceSession: true });
      });
    }, 2500);
    return () => {
      window.clearTimeout(t);
      if (stop) stop();
    };
  }, [suppressInteractiveUi]);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <I18nProvider>
              <ErrorBoundary>
                <div className="pb-14 md:pb-0">
                  <Suspense fallback={null}>
                    <OnboardingRedirect />
                  </Suspense>
                  <CheckoutResumer />
                  <AppRoutes />
                </div>
              </ErrorBoundary>

              {showDeferredUi && !suppressInteractiveUi && (
                <Suspense fallback={null}>
                  <MobileBottomNav />
                  <PushPermissionBanner />
                  <PWAInstallBanner />
                  <FeedbackWidget />
                </Suspense>
              )}
            </I18nProvider>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
